import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import BpmnEditor from '../components/BpmnEditor';
import ProcessSelector from '../components/ProcessSelector';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  ArrowLeft, 
  Save, 
  Loader2,
  X,
  Plus,
  CheckCircle,
  Link2,
  Unlink,
  Lightbulb,
  FolderKanban,
  GitBranch
} from 'lucide-react';
import { toast } from 'sonner';

const ProcessMapEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { user, isAdmin, isApprover } = useAuth();
  const isEditMode = !!id;

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [l2Processes, setL2Processes] = useState([]);
  const [l3Processes, setL3Processes] = useState([]);
  const [ideas, setIdeas] = useState([]);
  const [projects, setProjects] = useState([]);
  const [linkedIdeas, setLinkedIdeas] = useState([]);
  const [linkedProjects, setLinkedProjects] = useState([]);
  const [linkedProcessId, setLinkedProcessId] = useState(null);
  const [linkedProcessLevel, setLinkedProcessLevel] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    department: '',
    process_area: '',
    version: '1.0',
    tags: [],
    xml_content: '',
    status: 'Draft'
  });
  
  const [tagInput, setTagInput] = useState('');
  const [xmlContent, setXmlContent] = useState('');
  const [selectedIdeaToLink, setSelectedIdeaToLink] = useState('');
  const [selectedProjectToLink, setSelectedProjectToLink] = useState('');
  const [subprocessLinks, setSubprocessLinks] = useState([]);
  const [allProcesses, setAllProcesses] = useState([]);

  useEffect(() => {
    fetchSettings();
    fetchIdeasAndProjects();
    if (isEditMode) {
      fetchProcessMap();
    } else {
      fetchTemplate();
      // Check for pre-link params
      const linkType = searchParams.get('linkType');
      const linkId = searchParams.get('linkId');
      const linkProcess = searchParams.get('linkProcess');
      if (linkType && linkId) {
        if (linkType === 'idea') {
          setLinkedIdeas([linkId]);
        } else if (linkType === 'project') {
          setLinkedProjects([linkId]);
        }
      }
      if (linkProcess) setLinkedProcessId(linkProcess);
    }
  }, [id]);

  const fetchIdeasAndProjects = async () => {
    try {
      const [ideasRes, projectsRes] = await Promise.all([
        api.get('/ideas'),
        api.get('/projects')
      ]);
      setIdeas(ideasRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      console.error('Failed to fetch ideas/projects:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const procRes = await api.get('/processes');
      setL2Processes(procRes.data.filter(p => p.level === 2));
      setL3Processes(procRes.data.filter(p => p.level === 3));
      setAllProcesses(procRes.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const fetchTemplate = async () => {
    try {
      const response = await api.get('/process-maps/template');
      setXmlContent(response.data.xml);
      setFormData(prev => ({ ...prev, xml_content: response.data.xml }));
    } catch (error) {
      console.error('Failed to fetch template:', error);
    }
  };

  const fetchProcessMap = async () => {
    try {
      const response = await api.get(`/process-maps/${id}`);
      const data = response.data;
      setFormData({
        name: data.name || '',
        description: data.description || '',
        department: data.department || '',
        process_area: data.process_area || '',
        version: data.version || '1.0',
        tags: data.tags || [],
        xml_content: data.xml_content || '',
        status: data.status || 'Draft'
      });
      setXmlContent(data.xml_content || '');
      setLinkedIdeas(data.linked_ideas || []);
      setLinkedProjects(data.linked_projects || []);
      setLinkedProcessId(data.linked_process_id || null);
      setLinkedProcessLevel(data.linked_process_level || null);
      setSubprocessLinks(data.subprocess_links || []);
    } catch (error) {
      console.error('Failed to fetch process map:', error);
      toast.error('Failed to load process map');
      navigate('/process-maps');
    } finally {
      setLoading(false);
    }
  };

  const handleXmlChange = (newXml) => {
    setXmlContent(newXml);
    setFormData(prev => ({ ...prev, xml_content: newXml }));
  };

  const handleSave = async (xmlFromEditor) => {
    if (!formData.name.trim()) {
      toast.error('Please enter a name for the process map');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        xml_content: xmlFromEditor || xmlContent,
        linked_process_id: linkedProcessId,
        linked_process_level: linkedProcessLevel,
        subprocess_links: subprocessLinks,
      };

      let mapId = id;
      
      if (isEditMode) {
        await api.put(`/process-maps/${id}`, payload);
        toast.success('Process map updated');
      } else {
        const response = await api.post('/process-maps', payload);
        mapId = response.data.id;
        
        // Link to ideas/projects if any
        for (const ideaId of linkedIdeas) {
          await api.post(`/process-maps/${mapId}/link?entity_type=idea&entity_id=${ideaId}`);
        }
        for (const projectId of linkedProjects) {
          await api.post(`/process-maps/${mapId}/link?entity_type=project&entity_id=${projectId}`);
        }
        
        toast.success('Process map created');
        navigate(`/process-maps/${mapId}/edit`);
      }
    } catch (error) {
      console.error('Failed to save process map:', error);
      toast.error('Failed to save process map');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    try {
      await api.post(`/process-maps/${id}/publish`);
      toast.success('Process map published');
      setFormData(prev => ({ ...prev, status: 'Published' }));
    } catch (error) {
      console.error('Failed to publish:', error);
      toast.error('Failed to publish process map');
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleLinkIdea = async () => {
    if (!selectedIdeaToLink) return;
    if (isEditMode) {
      try {
        await api.post(`/process-maps/${id}/link?entity_type=idea&entity_id=${selectedIdeaToLink}`);
        setLinkedIdeas(prev => [...prev, selectedIdeaToLink]);
        setSelectedIdeaToLink('');
        toast.success('Idea linked');
      } catch (error) {
        toast.error('Failed to link idea');
      }
    } else {
      setLinkedIdeas(prev => [...prev, selectedIdeaToLink]);
      setSelectedIdeaToLink('');
    }
  };

  const handleUnlinkIdea = async (ideaId) => {
    if (isEditMode) {
      try {
        await api.post(`/process-maps/${id}/unlink?entity_type=idea&entity_id=${ideaId}`);
        setLinkedIdeas(prev => prev.filter(i => i !== ideaId));
        toast.success('Idea unlinked');
      } catch (error) {
        toast.error('Failed to unlink idea');
      }
    } else {
      setLinkedIdeas(prev => prev.filter(i => i !== ideaId));
    }
  };

  const handleLinkProject = async () => {
    if (!selectedProjectToLink) return;
    if (isEditMode) {
      try {
        await api.post(`/process-maps/${id}/link?entity_type=project&entity_id=${selectedProjectToLink}`);
        setLinkedProjects(prev => [...prev, selectedProjectToLink]);
        setSelectedProjectToLink('');
        toast.success('Project linked');
      } catch (error) {
        toast.error('Failed to link project');
      }
    } else {
      setLinkedProjects(prev => [...prev, selectedProjectToLink]);
      setSelectedProjectToLink('');
    }
  };

  const handleUnlinkProject = async (projectId) => {
    if (isEditMode) {
      try {
        await api.post(`/process-maps/${id}/unlink?entity_type=project&entity_id=${projectId}`);
        setLinkedProjects(prev => prev.filter(p => p !== projectId));
        toast.success('Project unlinked');
      } catch (error) {
        toast.error('Failed to unlink project');
      }
    } else {
      setLinkedProjects(prev => prev.filter(p => p !== projectId));
    }
  };

  const getIdeaName = (ideaId) => {
    const idea = ideas.find(i => i.id === ideaId);
    return idea ? idea.title : ideaId;
  };

  const getProjectName = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    return project ? project.project_name : projectId;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-[#235894]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="process-map-editor">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">
              {isEditMode ? 'Edit Process Map' : 'Create Process Map'}
            </h1>
            <p className="text-slate-500 text-sm">
              Design your business process using BPMN 2.0 notation
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isEditMode && formData.status === 'Draft' && (isApprover() || isAdmin()) && (
            <Button 
              variant="outline"
              onClick={handlePublish}
              data-testid="publish-btn"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Publish
            </Button>
          )}
          <Button 
            onClick={() => handleSave(xmlContent)}
            disabled={saving}
            className="bg-[#235894] hover:bg-[#1E4B80]"
            data-testid="save-process-map-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {isEditMode ? 'Save Changes' : 'Create Map'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Properties Panel */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg">Properties</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Invoice Processing Flow"
                data-testid="name-input"
              />
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of this process..."
                rows={3}
              />
            </div>

            <div>
              <Label htmlFor="department">Department (Value Stream)</Label>
              <Select
                value={formData.department}
                onValueChange={(value) => setFormData(prev => ({ ...prev, department: value, process_area: '' }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select department" />
                </SelectTrigger>
                <SelectContent>
                  {l2Processes.map((p) => (
                    <SelectItem key={p.id} value={p.name}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="process_area">Process Area (E2E Process)</Label>
              <Select
                value={formData.process_area}
                onValueChange={(value) => setFormData(prev => ({ ...prev, process_area: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder={formData.department ? "Select process area" : "Select department first"} />
                </SelectTrigger>
                <SelectContent>
                  {(() => {
                    const selectedL2 = l2Processes.find(p => p.name === formData.department);
                    const filtered = selectedL2
                      ? l3Processes.filter(p => p.parent_process_id === selectedL2.id)
                      : l3Processes;
                    return filtered.map((p) => (
                      <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>
                    ));
                  })()}
                </SelectContent>
              </Select>
            </div>

            <div>
              <ProcessSelector
                value={linkedProcessId}
                onChange={(procId, level) => { setLinkedProcessId(procId); setLinkedProcessLevel(level); }}
                label="Linked Process"
              />
            </div>

            <div>
              <Label htmlFor="version">Version</Label>
              <Input
                id="version"
                value={formData.version}
                onChange={(e) => setFormData(prev => ({ ...prev, version: e.target.value }))}
                placeholder="1.0"
              />
            </div>

            <div>
              <Label>Tags</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                  placeholder="Add tag..."
                  className="flex-1"
                />
                <Button variant="outline" size="icon" onClick={handleAddTag}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {formData.tags.map((tag, i) => (
                    <Badge key={i} variant="secondary" className="gap-1">
                      {tag}
                      <button onClick={() => handleRemoveTag(tag)}>
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {isEditMode && (
              <div className="pt-4 border-t">
                <Label>Status</Label>
                <Badge className={
                  formData.status === 'Published' 
                    ? 'bg-emerald-100 text-emerald-700' 
                    : 'bg-slate-100 text-slate-700'
                }>
                  {formData.status}
                </Badge>
              </div>
            )}

            {/* Link to Ideas */}
            <div className="pt-4 border-t">
              <Label className="flex items-center gap-2 mb-2">
                <Lightbulb className="w-4 h-4" />
                Linked Ideas
              </Label>
              {linkedIdeas.length > 0 && (
                <div className="space-y-1 mb-2">
                  {linkedIdeas.map((ideaId) => (
                    <div key={ideaId} className="flex items-center justify-between p-2 bg-amber-50 rounded text-sm">
                      <span className="truncate">{getIdeaName(ideaId)}</span>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-6 w-6"
                        onClick={() => handleUnlinkIdea(ideaId)}
                      >
                        <Unlink className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-1">
                <Select
                  value={selectedIdeaToLink}
                  onValueChange={setSelectedIdeaToLink}
                >
                  <SelectTrigger className="text-xs">
                    <SelectValue placeholder="Select idea..." />
                  </SelectTrigger>
                  <SelectContent>
                    {ideas.filter(i => !linkedIdeas.includes(i.id)).map((idea) => (
                      <SelectItem key={idea.id} value={idea.id}>
                        {idea.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="outline" size="icon" onClick={handleLinkIdea} disabled={!selectedIdeaToLink}>
                  <Link2 className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Link to Projects */}
            <div className="pt-4 border-t">
              <Label className="flex items-center gap-2 mb-2">
                <FolderKanban className="w-4 h-4" />
                Linked Projects
              </Label>
              {linkedProjects.length > 0 && (
                <div className="space-y-1 mb-2">
                  {linkedProjects.map((projectId) => (
                    <div key={projectId} className="flex items-center justify-between p-2 bg-blue-50 rounded text-sm">
                      <span className="truncate">{getProjectName(projectId)}</span>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-6 w-6"
                        onClick={() => handleUnlinkProject(projectId)}
                      >
                        <Unlink className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-1">
                <Select
                  value={selectedProjectToLink}
                  onValueChange={setSelectedProjectToLink}
                >
                  <SelectTrigger className="text-xs">
                    <SelectValue placeholder="Select project..." />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.filter(p => !linkedProjects.includes(p.id)).map((project) => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.project_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="outline" size="icon" onClick={handleLinkProject} disabled={!selectedProjectToLink}>
                  <Link2 className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Sub-Process Links */}
            {isEditMode && (
              <div className="pt-4 border-t">
                <Label className="flex items-center gap-2 mb-2">
                  <GitBranch className="w-4 h-4" />
                  Sub-Process Links
                </Label>
                <p className="text-xs text-slate-400 mb-2">Map BPMN elements to processes in the hierarchy</p>
                {subprocessLinks.length > 0 && (
                  <div className="space-y-1 mb-2">
                    {subprocessLinks.map((link, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-teal-50 rounded text-xs">
                        <div className="truncate">
                          <span className="font-medium">{link.bpmn_element_id}</span>
                          <span className="text-slate-400"> &rarr; </span>
                          <span>{link.process_name || link.process_id}</span>
                        </div>
                        <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => setSubprocessLinks(prev => prev.filter((_, j) => j !== i))}>
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="space-y-1">
                  <Input placeholder="BPMN Element ID" id="sp-element-id" className="text-xs h-8" />
                  <Select onValueChange={(procId) => {
                    const el = document.getElementById('sp-element-id');
                    if (!el?.value || !procId) return;
                    const proc = allProcesses.find(p => p.id === procId);
                    setSubprocessLinks(prev => [...prev, { bpmn_element_id: el.value, process_id: procId, process_name: proc?.name || procId }]);
                    el.value = '';
                  }}>
                    <SelectTrigger className="text-xs h-8"><SelectValue placeholder="Link to process..." /></SelectTrigger>
                    <SelectContent>
                      {allProcesses.map(p => (
                        <SelectItem key={p.id} value={p.id}>L{p.level} {p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* BPMN Editor */}
        <Card className="lg:col-span-3">
          <CardHeader className="pb-0">
            <CardTitle className="text-lg">Process Diagram</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="border-t">
              <BpmnEditor
                xml={xmlContent}
                onSave={handleSave}
                onXmlChange={handleXmlChange}
                height={600}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProcessMapEditor;
